import React, { useEffect, useState, useMemo } from 'react';
import {XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Bar, BarChart } from 'recharts';
import MultiSelectDropdown from '../multiselect/MultiSelectDropdown';
import { useProductionData } from '../../contexts/ProductionDataContext';
import { fetchProductionReport, fetchTableData } from '../../services/api';

const colors = ["rgb(156,39,176)", "rgb(255,152,0)", "rgb(76,175,80)", "#ff7300", "#ff0000", "#0000ff"]; 

/**
 * Universal SummaryChart component that works for all object types:
 * - development: shows advance (m) instead of tonnage
 * - stoping: shows tonnage, grade, content
 * - tramming: shows tonnage, grade, content
 * - hoisting: shows tonnage, grade, content
 */
const SummaryChart = ({ objectName, objectType, summary_field = 'Month' }) => {
  const [data, setData] = useState([]);
  const [alldata, setAllData] = useState({});
  const [filterData, setFilterData] = useState([[], summary_field, []]);
  const [summaryField, setSummaryField] = useState(summary_field);
  const [parsedData, setParsedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState('FY2025');
  
  // Date range filter - default to start of current month to yesterday
  const getDefaultStartDate = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0]; // YYYY-MM-DD format
  };
  
  const getDefaultEndDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0]; // YYYY-MM-DD format
  };
  
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [entityAdvance, setEntityAdvance] = useState(null);
  const [drillingSummary, setDrillingSummary] = useState(null);
  
  // Get production data from context
  const { loading: dataLoading } = useProductionData();
  
  // Determine metrics based on objectType
  const getMetricsForType = (type) => {
    switch (type) {
      case 'development':
        return {
          primaryMetric: 'advance',
          trackerMetricKey: 'tonnage', // production.php tracker uses tonnage key for advance_m
          primaryUnit: 'm',
          hasGrade: false,
          hasContent: false,
          entityField: 'dev_end_id'
        };
      case 'stoping':
        return {
          primaryMetric: 'tonnage',
          primaryUnit: 't',
          hasGrade: true,
          hasContent: true,
          entityField: 'stope_id'
        };
      case 'tramming':
        return {
          primaryMetric: 'tonnage',
          primaryUnit: 't',
          hasGrade: true,
          hasContent: true,
          entityField: 'box_id'
        };
      case 'hoisting':
        return {
          primaryMetric: 'tonnage',
          primaryUnit: 't',
          hasGrade: true,
          hasContent: true,
          entityField: 'hoist_fac_name'
        };
      default:
        return {
          primaryMetric: 'tonnage',
          primaryUnit: 't',
          hasGrade: true,
          hasContent: true,
          entityField: 'entity_id'
        };
    }
  };

  // Memoize metrics to prevent infinite loops - only recalculate when objectType changes
  const metrics = useMemo(() => getMetricsForType(objectType), [objectType]);
  
  // Fetch data from production.php API endpoint
  useEffect(() => {
    const fetchDatabaseData = async () => {
      if (!objectType || objectType === 'basic') {
        setParsedData([]);
        setEntityAdvance(null);
        setDrillingSummary(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch production report for the specific object type, filtered by objectName and date range at database level
        // This is much more efficient than fetching all data and filtering on the frontend
        const report = await fetchProductionReport(objectType, startDate, endDate, objectName || null);
        
        if (!report.success) {
          console.error(`Error fetching ${objectType} report:`, report.error);
          setParsedData([]);
          setEntityAdvance(null);
          setDrillingSummary(null);
          setLoading(false);
          return;
        }
        
        // Log the report structure for debugging
        console.log(`[SummaryChart] Report data structure for ${objectType}:`, {
          hasTracker: !!report.data?.tracker,
          hasEntities: !!report.data?.tracker?.entities,
          entitiesCount: report.data?.tracker?.entities?.length || 0,
          hasDays: !!report.data?.tracker?.days,
          daysCount: report.data?.tracker?.days?.length || 0,
          hasEntityAdvance: !!report.data?.entityAdvance,
          reportKeys: report.data ? Object.keys(report.data) : []
        });

        // Entity-scoped development: use entityAdvance for daily/cumulative charts
        if (objectType === 'development' && objectName && report.data?.entityAdvance) {
          setEntityAdvance(report.data.entityAdvance);
        } else if (objectType === 'development' && objectName && report.data?.trend) {
          // Fallback: build cumulative from filtered trend
          let cumActual = 0;
          let cumBudget = 0;
          const days = [...report.data.trend]
            .sort((a, b) => String(a.date).localeCompare(String(b.date)))
            .map(entry => {
              const actual = Number(entry.actualTonnage) || 0;
              const budget = Number(entry.budgetTonnage) || 0;
              cumActual += actual;
              cumBudget += budget;
              return {
                date: entry.date,
                actual,
                budget,
                cumulativeActual: cumActual,
                cumulativeBudget: cumBudget
              };
            });
          setEntityAdvance({
            unit: 'm',
            days,
            periodTotal: { actual: cumActual, budget: cumBudget }
          });
        } else {
          setEntityAdvance(null);
        }
        
        // Data is already filtered by objectName at the database level, so no need to filter again
        // Transform production report data to universal structure
        const transformedData = transformProductionReportData(report.data, objectType, metrics);
        
        console.log(`[SummaryChart] Transformed data count:`, transformedData?.length || 0);
        
        // Extract fiscal year(s) dynamically from the data
        if (transformedData && transformedData.length > 0) {
          const years = new Set();
          transformedData.forEach(row => {
            if (row.Date) {
              const parts = row.Date.split('/');
              if (parts.length === 3) {
                const year = parseInt(parts[2]);
                if (!isNaN(year)) {
                  years.add(year);
                }
              }
            }
          });
          
          const sortedYears = Array.from(years).sort((a, b) => b - a);
          const latestYear = sortedYears.length > 0 ? sortedYears[0] : new Date().getFullYear();
          setFiscalYear(`FY${latestYear}`);
        }
        
        setParsedData(transformedData);

        // Development drilling summary: sum metrics from drill-blast table for this end + date range
        if (objectType === 'development' && objectName) {
          try {
            const drillResult = await fetchTableData('tblLogUGDevelopmentDrillBlast', {
              start_date: startDate,
              end_date: endDate
            });
            if (drillResult.success && Array.isArray(drillResult.data)) {
              const filtered = drillResult.data.filter(row => {
                const id = row.dev_end_id || row.EndID || row.StopeID;
                return id && String(id) === String(objectName);
              });
              setDrillingSummary(summarizeDevelopmentDrilling(filtered, startDate, endDate));
            } else {
              setDrillingSummary(summarizeDevelopmentDrilling([], startDate, endDate));
            }
          } catch (drillErr) {
            console.error('[SummaryChart] Error fetching development drilling summary:', drillErr);
            setDrillingSummary(summarizeDevelopmentDrilling([], startDate, endDate));
          }
        } else {
          setDrillingSummary(null);
        }
      } catch (error) {
        console.error('Error fetching database data:', error);
        setParsedData([]);
        setEntityAdvance(null);
        setDrillingSummary(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDatabaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, objectName, startDate, endDate]); // Include date range in dependencies
  
  // Transform production report data to universal structure
  const transformProductionReportData = (reportData, type, metricsConfig) => {
    const dataMap = new Map();
    
    // Helper to get date string in DD/MM/YYYY format
    const getDateStr = (dateStr) => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    };
    
    // Helper to extract level from entity ID (e.g., "1L" from "1L_STOPE01" or "1L_DEV01")
    const getLevelFromEntityId = (entityId) => {
      if (!entityId) return '';
      const match = entityId.match(/^(\d+L)/);
      return match ? match[1] : '';
    };
    
    // Process tracker data from report
    if (reportData && reportData.tracker && reportData.tracker.entities) {
      reportData.tracker.entities.forEach(entity => {
        const entityId = entity.entityId || '';
        const level = getLevelFromEntityId(entityId);
        
        // Process each day's data for this entity
        if (reportData.tracker.days && entity.rows) {
          reportData.tracker.days.forEach(day => {
            const dateStr = getDateStr(day);
            if (!dateStr) return;
            
            const key = `${entityId}_${dateStr}`;
            
            // Get actual and budget values based on metrics
            // Development tracker uses metricKey "tonnage" for advance_m
            const trackerKey = metricsConfig.trackerMetricKey || metricsConfig.primaryMetric;
            const actualPrimaryRow = entity.rows.find(r => 
              (r.metricKey === trackerKey || r.metricKey === metricsConfig.primaryMetric) && r.metricType === 'Actual'
            );
            const budgetPrimaryRow = entity.rows.find(r => 
              (r.metricKey === trackerKey || r.metricKey === metricsConfig.primaryMetric) && r.metricType === 'Budget'
            );
            
            const actualPrimary = actualPrimaryRow?.values?.[day] || 0;
            const budgetPrimary = budgetPrimaryRow?.values?.[day] || 0;
            
            // Get grade and content if available
            let actualGrade = 0;
            let budgetGrade = 0;
            let actualContent = 0;
            let budgetContent = 0;
            
            if (metricsConfig.hasGrade) {
              const actualGradeRow = entity.rows.find(r => r.metricKey === 'grade' && r.metricType === 'Actual');
              const budgetGradeRow = entity.rows.find(r => r.metricKey === 'grade' && r.metricType === 'Budget');
              actualGrade = actualGradeRow?.values?.[day] || 0;
              budgetGrade = budgetGradeRow?.values?.[day] || 0;
            }
            
            if (metricsConfig.hasContent) {
              const actualContentRow = entity.rows.find(r => r.metricKey === 'content' && r.metricType === 'Actual');
              const budgetContentRow = entity.rows.find(r => r.metricKey === 'content' && r.metricType === 'Budget');
              // Content is in grams, convert to kg by dividing by 1000
              actualContent = (actualContentRow?.values?.[day] || 0) / 1000;
              budgetContent = (budgetContentRow?.values?.[day] || 0) / 1000;
            }
            
            if (actualPrimary > 0 || budgetPrimary > 0) {
              if (!dataMap.has(key)) {
                const record = {
                  ID: entityId,
                  Date: dateStr,
                  Level: level,
                  Plan_t: 0,
                  Plan_gpt: 0,
                  Plan_kgs: 0,
                  Budget_t: 0,
                  Budget_gpt: 0,
                  Budget_kgs: 0,
                  Actual_t: 0,
                  Actual_gpt: 0,
                  Actual_kgs: 0
                };
                
                // For development, use 'm' suffix instead of 't'
                if (type === 'development') {
                  record.Budget_m = 0;
                  record.Actual_m = 0;
                }
                
                dataMap.set(key, record);
              }
              
              const record = dataMap.get(key);
              
              // Set values based on type
              if (type === 'development') {
                record.Actual_m += actualPrimary;
                record.Budget_m += budgetPrimary;
              } else {
                record.Actual_t += actualPrimary;
                record.Budget_t += budgetPrimary;
                record.Actual_gpt = actualGrade || record.Actual_gpt || 0;
                record.Budget_gpt = budgetGrade || record.Budget_gpt || 0;
                record.Actual_kgs += actualContent;
                record.Budget_kgs += budgetContent;
              }
            }
          });
        }
      });
    }
    
    // Calculate grades from content if missing
    dataMap.forEach((record) => {
      if (type !== 'development') {
        if (record.Budget_t > 0 && record.Budget_kgs > 0 && !record.Budget_gpt) {
          record.Budget_gpt = (record.Budget_kgs * 1000) / record.Budget_t;
        }
        if (record.Actual_t > 0 && record.Actual_kgs > 0 && !record.Actual_gpt) {
          record.Actual_gpt = (record.Actual_kgs * 1000) / record.Actual_t;
        }
      }
    });
    
    return Array.from(dataMap.values());
  };

  // Process data when parsedData, objectName, or filters change
  useEffect(() => {     
    if(!parsedData || loading || dataLoading) return;

    console.log('SummaryChart parsedData:', parsedData);
    // Note: Data is already filtered by objectName at fetch level, so no need to filter again here
    let filteredData = parsedData;

    // Apply level filter if set
    if(filterData[0].length > 0){
      filteredData = filteredData.filter(row => filterData[0].includes(row['Level']));
    }

    // Apply period filter if set
    if(filterData[2].length > 0){
      filteredData = filteredData.filter(row => filterData[2].includes(row[filterData[1]]));
    }

    console.log('SummaryChart filteredData:', filteredData);

    // Add Day, Month, Quarter, Year fields to each row
    const processedData = filteredData.map(row => {
      const dateStr = row.Date;
      let date = null;
      
      if (dateStr) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const year = parseInt(parts[2]);
          date = new Date(year, month, day);
        }
      }

      return {
        ...row,
        Day: dateStr || (date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''),
        Month: date ? date.toLocaleString('default', { month: 'long' }) + ' ' + date.getFullYear() : '',
        Quarter: date ? `Q${Math.floor((date.getMonth() + 3) / 3)} ${date.getFullYear()}` : '',
        Year: date ? date.getFullYear().toString() : ''
      };
    });

    // Create dynamic field names based on fiscal year
    const planTonnageKey = `${fiscalYear}_Plan_${metrics.primaryMetric === 'advance' ? 'm' : 't'}`;
    const planGradeKey = `${fiscalYear}_Plan_gpt`;
    const planContentKey = `${fiscalYear}_Plan_kgs`;
    const planTonnageSumKey = `${fiscalYear}_Plan_${metrics.primaryMetric === 'advance' ? 'm' : 't'}_Sum`;
    
    const summary = {};
    const totalSummary = {
      [planTonnageKey]: 0,
      Planned_Primary: 0,
      Actual_Primary: 0,
      [planTonnageSumKey]: 0,
      Planned_Primary_Sum: 0,
      Actual_Primary_Sum: 0
    };
    
    // Add grade and content fields if applicable
    if (metrics.hasGrade) {
      totalSummary[planGradeKey] = 0;
      totalSummary.Planned_Grade = 0;
      totalSummary.Actual_Grade = 0;
      totalSummary.Planned_Primary_Sum_WithGrade = 0; // Track tonnage with valid grade for weighted average
      totalSummary.Actual_Primary_Sum_WithGrade = 0; // Track tonnage with valid grade for weighted average
    }
    
    if (metrics.hasContent) {
      totalSummary[planContentKey] = 0;
      totalSummary.Planned_Content = 0;
      totalSummary.Actual_Content = 0;
    }

    processedData.forEach(row => {
      const key = row[summaryField];
      if (!summary[key]) {
        summary[key] = {
          [planTonnageKey]: 0,
          Planned_Primary: 0,
          Actual_Primary: 0,
          [planTonnageSumKey]: 0,
          Planned_Primary_Sum: 0,
          Actual_Primary_Sum: 0
        };
        
        if (metrics.hasGrade) {
          summary[key][planGradeKey] = 0;
          summary[key].Planned_Grade = 0;
          summary[key].Actual_Grade = 0;
          summary[key].Planned_Primary_Sum_WithGrade = 0; // Track tonnage with valid grade for weighted average
          summary[key].Actual_Primary_Sum_WithGrade = 0; // Track tonnage with valid grade for weighted average
        }
        
        if (metrics.hasContent) {
          summary[key][planContentKey] = 0;
          summary[key].Planned_Content = 0;
          summary[key].Actual_Content = 0;
        }
      }
      
      // Get primary metric value (tonnage or advance)
      const primaryField = metrics.primaryMetric === 'advance' ? 'm' : 't';
      const budgetPrimary = parseFloat(row[`Budget_${primaryField}`] || 0);
      const actualPrimary = parseFloat(row[`Actual_${primaryField}`] || 0);
      
      // Accumulate primary metric
      summary[key].Planned_Primary += budgetPrimary;
      summary[key].Actual_Primary += actualPrimary;
      summary[key].Planned_Primary_Sum += budgetPrimary;
      summary[key].Actual_Primary_Sum += actualPrimary;
      
      totalSummary.Planned_Primary += budgetPrimary;
      totalSummary.Actual_Primary += actualPrimary;
      totalSummary.Planned_Primary_Sum += budgetPrimary;
      totalSummary.Actual_Primary_Sum += actualPrimary;
      
      // Accumulate grade if available - only include records with valid grade (> 0)
      if (metrics.hasGrade) {
        const budgetGrade = parseFloat(row['Budget_gpt'] || 0);
        const actualGrade = parseFloat(row['Actual_gpt'] || 0);
        
        // Only accumulate grade and tonnage if grade is greater than 0
        if (budgetGrade > 0 && budgetPrimary > 0) {
          summary[key].Planned_Grade += budgetGrade * budgetPrimary;
          summary[key].Planned_Primary_Sum_WithGrade += budgetPrimary; // Track tonnage with valid grade
          totalSummary.Planned_Grade += budgetGrade * budgetPrimary;
          totalSummary.Planned_Primary_Sum_WithGrade += budgetPrimary;
        }
        
        if (actualGrade > 0 && actualPrimary > 0) {
          summary[key].Actual_Grade += actualGrade * actualPrimary;
          summary[key].Actual_Primary_Sum_WithGrade += actualPrimary; // Track tonnage with valid grade
          totalSummary.Actual_Grade += actualGrade * actualPrimary;
          totalSummary.Actual_Primary_Sum_WithGrade += actualPrimary;
        }
      }
      
      // Accumulate content if available
      // Note: Content is already converted from grams to kg in transformProductionReportData
      if (metrics.hasContent) {
        const budgetContent = parseFloat(row['Budget_kgs'] || 0);
        const actualContent = parseFloat(row['Actual_kgs'] || 0);
        
        summary[key].Planned_Content += budgetContent;
        summary[key].Actual_Content += actualContent;
        
        totalSummary.Planned_Content += budgetContent;
        totalSummary.Actual_Content += actualContent;
      }
    });

    // Calculate weighted averages for grade - only use tonnage with valid grade (> 0)
    if (metrics.hasGrade) {
      totalSummary.Planned_Grade = totalSummary.Planned_Primary_Sum_WithGrade > 0 
        ? totalSummary.Planned_Grade / totalSummary.Planned_Primary_Sum_WithGrade 
        : 0;
      totalSummary.Actual_Grade = totalSummary.Actual_Primary_Sum_WithGrade > 0 
        ? totalSummary.Actual_Grade / totalSummary.Actual_Primary_Sum_WithGrade 
        : 0;
    }

    const summaryData = Object.keys(summary).map(key => {
      const record = {
        [summaryField]: key,
        Planned_Primary: summary[key].Planned_Primary,
        Actual_Primary: summary[key].Actual_Primary
      };
      
      if (metrics.hasGrade) {
        // Only calculate grade using tonnage that has valid grade (> 0)
        record.Planned_Grade = summary[key].Planned_Primary_Sum_WithGrade > 0 
          ? Number((summary[key].Planned_Grade / summary[key].Planned_Primary_Sum_WithGrade).toFixed(2)) 
          : 0;
        record.Actual_Grade = summary[key].Actual_Primary_Sum_WithGrade > 0 
          ? Number((summary[key].Actual_Grade / summary[key].Actual_Primary_Sum_WithGrade).toFixed(2)) 
          : 0;
      }
      
      if (metrics.hasContent) {
        record.Planned_Content = summary[key].Planned_Content;
        record.Actual_Content = summary[key].Actual_Content;
      }
      
      return record;
    });

    setAllData(totalSummary);
    setData(summaryData);

    console.log('SummaryChart summaryData:', summaryData);
  
  }, [objectName, summaryField, filterData, parsedData, loading, dataLoading, fiscalYear, objectType, metrics]);

  const dailyAdvanceChartData = useMemo(() => {
    if (!entityAdvance?.days) return [];
    return entityAdvance.days.map(d => ({
      Day: d.date,
      Planned_Primary: d.budget,
      Actual_Primary: d.actual
    }));
  }, [entityAdvance]);

  const cumulativeAdvanceChartData = useMemo(() => {
    if (!entityAdvance?.days) return [];
    return entityAdvance.days.map(d => ({
      Day: d.date,
      Planned_Primary: d.cumulativeBudget,
      Actual_Primary: d.cumulativeActual
    }));
  }, [entityAdvance]);

  if (loading || dataLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-2 text-zinc-400">Loading production data...</span>
      </div>
    );
  }

  if (!objectType || objectType === 'basic') {
    return (
      <div className="flex items-center justify-center p-8 text-zinc-500">
        <p>No data available for basic object type</p>
      </div>
    );
  }

  // Get type-specific labels
  const getTypeLabels = () => {
    switch (objectType) {
      case 'development':
        return {
          primaryLabel: 'Advance',
          primaryUnit: 'm',
          hasGrade: false,
          hasContent: false
        };
      case 'stoping':
        return {
          primaryLabel: 'Tonnage',
          primaryUnit: 't',
          hasGrade: true,
          hasContent: true
        };
      case 'tramming':
        return {
          primaryLabel: 'Tonnage',
          primaryUnit: 't',
          hasGrade: true,
          hasContent: true
        };
      case 'hoisting':
        return {
          primaryLabel: 'Tonnage',
          primaryUnit: 't',
          hasGrade: true,
          hasContent: true
        };
      default:
        return {
          primaryLabel: 'Tonnage',
          primaryUnit: 't',
          hasGrade: true,
          hasContent: true
        };
    }
  };

  const labels = getTypeLabels();
  const isEntityDevelopment = objectType === 'development' && !!objectName;

  return (
    <div className="relative w-full h-full bg-zinc-950 z-10">
      {!objectName && <FilterCard parsedData={parsedData} setFilterData={setFilterData} objectType={objectType} />}
      
      {isEntityDevelopment ? (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
            <label className="text-zinc-500">From</label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 px-1.5 py-0.5 text-xs"
            />
            <label className="text-zinc-500">To</label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 px-1.5 py-0.5 text-xs"
            />
            <button
              type="button"
              onClick={() => {
                setStartDate(getDefaultStartDate());
                setEndDate(getDefaultEndDate());
              }}
              className="m-0 px-2 py-0.5 text-[10px] bg-zinc-900 hover:bg-yellow-500/10 text-yellow-500 rounded-sm border border-yellow-600/35"
            >
              Reset
            </button>
          </div>

          <div className="overflow-x-auto border border-yellow-600/25 rounded mb-2">
            <table className="min-w-full text-xs">
              <thead className="bg-zinc-900/80">
                <tr>
                  <th className="px-1.5 py-1 text-left font-medium text-zinc-500 border-r border-zinc-800">Metric</th>
                  <th className="px-1.5 py-1 text-right font-medium text-zinc-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Date Range', drillingSummary?.dateRange ?? 'N/A'],
                  ['Advance (m)', Number(drillingSummary?.advanceM ?? entityAdvance?.periodTotal?.actual ?? 0).toFixed(2)],
                  ['Budget Advance (m)', Number(entityAdvance?.periodTotal?.budget ?? 0).toFixed(2)],
                  ['Holes Drilled', drillingSummary?.holesDrilled ?? 0],
                  ['Holes Charged', drillingSummary?.holesCharged ?? 0],
                  ['Holes Depth (m)', Number(drillingSummary?.holesDepth ?? 0).toFixed(2)],
                  ['Emex', Number(drillingSummary?.explosivesEmex ?? 0).toFixed(2)],
                  ['EZ Stoppers', Number(drillingSummary?.explosivesEZStoppers ?? 0).toFixed(2)],
                  ['IED', Number(drillingSummary?.explosivesIED ?? 0).toFixed(2)],
                  ['IG Cord', Number(drillingSummary?.explosivesIGCord ?? 0).toFixed(2)],
                  ['Operators', drillingSummary?.machineOperator ?? 0],
                  ['Est. Tonnage (t)', Number(drillingSummary?.estimatedTonnage ?? 0).toFixed(2)],
                  ['Est. Powder Factor', Number(drillingSummary?.estimatedPowderFactor ?? 0).toFixed(2)],
                  ['Est. Explosives (kg)', Number(drillingSummary?.estimatedExplosives ?? 0).toFixed(2)],
                ].map(([label, value], i) => (
                  <tr key={label} className={i % 2 === 0 ? 'bg-zinc-950' : 'bg-zinc-900/80'}>
                    <td className="px-1.5 py-0.5 font-medium text-zinc-300 border-r border-zinc-800">{label}</td>
                    <td className="px-1.5 py-0.5 text-right text-zinc-100 tabular-nums">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <CompactChart title="Daily Advance (m)" data={dailyAdvanceChartData} />
          <CompactChart title="Cumulative Advance (m)" data={cumulativeAdvanceChartData} />
        </>
      ) : (
        <>
      {/* Date Range Filter */}
      <div className="flex items-center gap-4 p-4 bg-zinc-900/80 border-b border-yellow-600/25">
        <div className="flex items-center gap-2">
          <label htmlFor="startDate" className="text-sm font-medium text-zinc-300 whitespace-nowrap">
            Start Date:
          </label>
          <input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="p-2 border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 focus:outline-none focus:ring-1 focus:ring-yellow-500 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="endDate" className="text-sm font-medium text-zinc-300 whitespace-nowrap">
            End Date:
          </label>
          <input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]} // Can't select future dates
            className="p-2 border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 focus:outline-none focus:ring-1 focus:ring-yellow-500 text-sm"
          />
        </div>
        <button
          onClick={() => {
            setStartDate(getDefaultStartDate());
            setEndDate(getDefaultEndDate());
          }}
          className="m-0 px-3 py-1.5 text-xs bg-zinc-900 hover:bg-yellow-500/10 text-yellow-500 rounded-sm border border-yellow-600/35 transition-colors"
        >
          Reset to Default
        </button>
      </div>

          <select
            value={summaryField}
            onChange={(e) => setSummaryField(e.target.value)}
            className="p-2 border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 focus:outline-none focus:ring-1 focus:ring-yellow-500 m-4 text-sm"
          >
            <option value="Day">Day</option>
            <option value="Month">Month</option>
            <option value="Quarter">Quarter</option>
            <option value="Year">Year</option>
          </select>

          {/* Summary Table */}
          <div className="bg-zinc-950 rounded-lg shadow-sm border border-yellow-600/25 mb-6">
            <h3 className="text-sm font-semibold text-zinc-300 mb-2 p-3 border-b border-yellow-600/25">
              Summary Statistics - {objectType.charAt(0).toUpperCase() + objectType.slice(1)}
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-800 border border-yellow-600/25 text-xs">
                <thead className="bg-zinc-900/80">
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Metric</th>
                    <th className="px-2 py-1 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-yellow-600/25">Budget</th>
                    <th className="px-2 py-1 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider">Actual</th>
                  </tr>
                </thead>
                <tbody className="bg-zinc-950 divide-y divide-zinc-800">
                  <tr className="hover:bg-yellow-500/5">
                    <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">{labels.primaryLabel}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">
                      {Number(alldata.Planned_Primary || 0).toLocaleString("en-EN", { maximumFractionDigits: 0 })} {labels.primaryUnit}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">
                      {Number(alldata.Actual_Primary || 0).toLocaleString("en-EN", { maximumFractionDigits: 0 })} {labels.primaryUnit}
                    </td>
                  </tr>
                  {labels.hasGrade && (
                    <tr className="hover:bg-yellow-500/5">
                      <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Grade</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">
                        {Number(alldata.Planned_Grade || 0).toFixed(2)} g/t
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">
                        {Number(alldata.Actual_Grade || 0).toFixed(2)} g/t
                      </td>
                    </tr>
                  )}
                  {labels.hasContent && (
                    <tr className="hover:bg-yellow-500/5">
                      <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-zinc-100 border-r border-yellow-600/25">Content</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center border-r border-yellow-600/25">
                        {Number(alldata.Planned_Content || 0).toLocaleString("en-EN", { maximumFractionDigits: 1 })} kg
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-zinc-100 text-center">
                        {Number(alldata.Actual_Content || 0).toLocaleString("en-EN", { maximumFractionDigits: 1 })} kg
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Primary Metric Trend Chart */}
          <GridItem title={`${labels.primaryLabel} Trend`} key={1}>
            <ChartComponent
              data={data}
              summary_field={summaryField}
              target_fields={[
                { graphKey: 'Planned_Primary', legend_name: 'Budget' },
                { graphKey: 'Actual_Primary', legend_name: 'Actual' }
              ]}
              graphOptions={{
                graph_type: 'BarChart',
                graph_title: `${labels.primaryLabel} Trend`,
                x_title: summaryField,
                y_title: `${labels.primaryLabel} (${labels.primaryUnit})`
              }}
            />
          </GridItem>

          {/* Grade Trend Chart (if applicable) */}
          {labels.hasGrade && (
            <GridItem title="Grade Trend" key={2}>
              <ChartComponent
                data={data}
                summary_field={summaryField}
                target_fields={[
                  { graphKey: 'Planned_Grade', legend_name: 'Budget Grade' },
                  { graphKey: 'Actual_Grade', legend_name: 'Actual Grade' }
                ]}
                graphOptions={{
                  graph_type: 'BarChart',
                  graph_title: 'Grade Trend',
                  x_title: summaryField,
                  y_title: 'Grade (g/t)'
                }}
              />
            </GridItem>
          )}

          {/* Content Trend Chart (if applicable) */}
          {labels.hasContent && (
            <GridItem title="Content Trend" key={3}>
              <ChartComponent
                data={data}
                summary_field={summaryField}
                target_fields={[
                  { graphKey: 'Planned_Content', legend_name: 'Budget Content' },
                  { graphKey: 'Actual_Content', legend_name: 'Actual Content' }
                ]}
                graphOptions={{
                  graph_type: 'BarChart',
                  graph_title: 'Content Trend',
                  x_title: summaryField,
                  y_title: 'Gold (kg)'
                }}
              />
            </GridItem>
          )}
        </>
      )}
    </div>
  );
};

// Chart component for rendering graphs
const ChartComponent = ({ data, summary_field, target_fields, graphOptions }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-zinc-500">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%" className="relative z-20">
      <BarChart
        data={data}
        margin={{
          top: 8,
          right: 8,
          bottom: 8,
          left: 0,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
        <XAxis 
          dataKey={summary_field}  
          fontSize={10}  
          tick={{ fill: '#a1a1aa' }} 
          label={{ value: summary_field, position: 'insideBottomRight', offset: 0 }} 
        />
        <YAxis  
          fontSize={10}  
          tick={{ fill: '#a1a1aa' }} 
          tickFormatter={graphOptions.y_title.includes('Grade') ? (v) => v.toFixed(2) : (v) => v.toLocaleString()} 
          label={{ value: graphOptions.y_title, angle: -90, position: 'insideLeft' }} 
        />      
        <Tooltip content={CustomTooltip} />
        <Legend fontSize={8} />
      
        {target_fields.map((field, index) => (
          <Bar key={field.graphKey} dataKey={field.graphKey} fill={colors[index]} name={field.legend_name}/>
        ))} 
      </BarChart>
    </ResponsiveContainer>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-2 bg-zinc-950 border border-yellow-600/35 flex flex-col gap-1 rounded-sm">
        <p className="text-xs font-semibold text-yellow-400">{`${label}`}</p>
        {
          payload.map((item, index) => {
            return (
              <p className="text-sm text-yellow-400" key={index}>
                {item.name}:
                <span className="sm-2"> {Number(item.value).toLocaleString("en-EN", { maximumFractionDigits: item.name.includes('Grade') ? 2 : 2 })}</span>
              </p>
            );
          })
        }
      </div>
    );
  }

  return null;
};

function GridItem({ title, children }) {
  return (
    <div className='flex flex-col items-center justify-center border border-yellow-600/30 bg-zinc-950 min-h-96 h-96 rounded-md shadow-lg mt-4'>
      <h3 className='text-xs font-semibold uppercase tracking-wider text-yellow-400 mb-1'>{title}</h3>
      <div className='w-full h-80 flex-1'>
        {children}
      </div>
    </div>
  );
}

function CompactChart({ title, data }) {
  return (
    <div className="border border-yellow-600/25 rounded mb-2 bg-zinc-950">
      <div className="px-2 py-1 text-[11px] font-semibold text-white border-b border-yellow-600/30">{title}</div>
      <div className="h-52 px-1 pb-1">
        <ChartComponent
          data={data}
          summary_field="Day"
          target_fields={[
            { graphKey: 'Planned_Primary', legend_name: 'Budget' },
            { graphKey: 'Actual_Primary', legend_name: 'Actual' }
          ]}
          graphOptions={{
            graph_type: 'BarChart',
            graph_title: title,
            x_title: 'Day',
            y_title: title
          }}
        />
      </div>
    </div>
  );
}

function FilterCard({ parsedData, setFilterData, objectType }) {
  const [selectedLevels, setSelectedLevels] = useState(['1L', '2L', '3L', '4L', '5L', '6L']);
  const [period, setPeriod] = useState('Month');
  const [selectedPeriod, setSelectedPeriod] = useState([]);
  const [selectPeriodOptions, setSelectPeriodOptions] = useState([]);

  const levels = ['1L', '2L', '3L', '4L', '5L', '6L'];
  const periods = ['Day', 'Month', 'Quarter', 'Year'];

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  useEffect(() => {
    console.log(selectedLevels, period, selectedPeriod);
    setFilterData([selectedLevels, period, selectedPeriod]);
  }, [selectedLevels, period, selectedPeriod, setFilterData]);

  useEffect(() => {
    if (!parsedData || !parsedData.length > 0) return;
    setSelectedPeriod([]);
    
    // Add Day, Month, Quarter, Year fields to parsedData for filtering
    const processedData = parsedData.map(row => {
      const dateStr = row.Date;
      let date = null;
      
      if (dateStr) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const year = parseInt(parts[2]);
          date = new Date(year, month, day);
        }
      }

      return {
        ...row,
        Day: dateStr || (date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''),
        Month: date ? date.toLocaleString('default', { month: 'long' }) + ' ' + date.getFullYear() : '',
        Quarter: date ? `Q${Math.floor((date.getMonth() + 3) / 3)} ${date.getFullYear()}` : '',
        Year: date ? date.getFullYear().toString() : ''
      };
    });
    
    // Extract unique periods dynamically from the data
    const uniquePeriods = [...new Set(processedData.map((item) => item[period]).filter(Boolean))];
    
    // Sort periods appropriately based on type
    let sortedPeriods = uniquePeriods;
    if (period === 'Year') {
      sortedPeriods = uniquePeriods.sort((a, b) => parseInt(a) - parseInt(b));
    } else if (period === 'Quarter') {
      sortedPeriods = uniquePeriods.sort((a, b) => {
        const aMatch = a.match(/Q(\d+)\s+(\d+)/);
        const bMatch = b.match(/Q(\d+)\s+(\d+)/);
        if (!aMatch || !bMatch) return a.localeCompare(b);
        const aYear = parseInt(aMatch[2]);
        const bYear = parseInt(bMatch[2]);
        if (aYear !== bYear) return aYear - bYear;
        return parseInt(aMatch[1]) - parseInt(bMatch[1]);
      });
    } else if (period === 'Month') {
      sortedPeriods = uniquePeriods.sort((a, b) => {
        const aDate = new Date(a);
        const bDate = new Date(b);
        if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) return a.localeCompare(b);
        return aDate.getTime() - bDate.getTime();
      });
    } else if (period === 'Day') {
      // Sort days chronologically (DD/MM/YYYY format)
      sortedPeriods = uniquePeriods.sort((a, b) => {
        const parseDate = (dateStr) => {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1;
            const year = parseInt(parts[2]);
            return new Date(year, month, day);
          }
          return new Date(dateStr);
        };
        const aDate = parseDate(a);
        const bDate = parseDate(b);
        if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) return a.localeCompare(b);
        return aDate.getTime() - bDate.getTime();
      });
    }
    
    console.log('Dynamic periods:', sortedPeriods);
    setSelectPeriodOptions(sortedPeriods);
  }, [period, parsedData]);

  if (!parsedData || !parsedData.length > 0) return null;

  const itemClickedPeriod = (item) => {
    setSelectedPeriod((prevSelected) => 
      prevSelected.includes(item) 
        ? prevSelected.filter((i) => i !== item) 
        : [...prevSelected, item]
    );
  };

  const itemClickedLevel = (item) => {
    setSelectedLevels((prevSelected) => 
      prevSelected.includes(item) 
        ? prevSelected.filter((i) => i !== item) 
        : [...prevSelected, item]
    );
  };

  return (
    <div className="p-4 bg-zinc-950 border border-yellow-600/25 rounded-sm">
      <div className='mb-4 mt-1 block w-full'>
        <MultiSelectDropdown 
          label={'LEVEL'} 
          list={levels} 
          selectedlist={selectedLevels} 
          itemClick={itemClickedLevel} 
        />
      </div>
      <div className="mb-4 mt-1 block w-full">
        <div className='cursor-pointer border-solid p-4 border-yellow-600/35 shadow-lg w-full border'>
          <select value={period} onChange={handlePeriodChange} className="mt-1 block w-full bg-zinc-950 text-zinc-200 border border-yellow-600/35 rounded-sm px-2 py-1 text-xs">
            {periods.map((prd) => (
              <option key={prd} value={prd}>
                {prd}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='mb-4 mt-1 block w-full'>
        <MultiSelectDropdown 
          label={'Options'} 
          list={selectPeriodOptions} 
          selectedlist={selectedPeriod} 
          itemClick={itemClickedPeriod} 
        />
      </div>
    </div>
  );
}

function summarizeDevelopmentDrilling(rows, startDate, endDate) {
  const empty = {
    dateRange: startDate && endDate ? `${startDate} to ${endDate}` : 'N/A',
    advanceM: 0,
    holesDrilled: 0,
    holesCharged: 0,
    holesDepth: 0,
    explosivesEmex: 0,
    explosivesEZStoppers: 0,
    explosivesIED: 0,
    explosivesIGCord: 0,
    machineOperator: 0,
    estimatedTonnage: 0,
    estimatedPowderFactor: 0,
    estimatedExplosives: 0
  };

  if (!rows || rows.length === 0) {
    return empty;
  }

  const num = (v) => parseFloat(v) || 0;
  const dates = rows
    .map(r => new Date(r.advance_date || r.DateDeveloped))
    .filter(d => !isNaN(d.getTime()));

  const operators = [...new Set(rows.map(r => r.machine_oper_id || r.MachineOperator).filter(Boolean))];

  let dateRange = empty.dateRange;
  if (dates.length > 0) {
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    dateRange = `${min.toISOString().slice(0, 10)} to ${max.toISOString().slice(0, 10)}`;
  }

  return {
    dateRange,
    advanceM: rows.reduce((s, r) => s + num(r.advance_m), 0),
    holesDrilled: rows.reduce((s, r) => s + num(r.holes_drilled_num || r.HolesDrilled), 0),
    holesCharged: rows.reduce((s, r) => s + num(r.holes_charged_num || r.HolesCharged), 0),
    holesDepth: rows.reduce((s, r) => s + num(r.hole_depth_m || r.HolesDepth), 0),
    explosivesEmex: rows.reduce((s, r) => s + num(r.emex_sticks_num || r.ExplosivesEmex), 0),
    explosivesEZStoppers: rows.reduce((s, r) => s + num(r.shock_tubes_num || r.ExplosivesEZStoppers), 0),
    explosivesIED: rows.reduce((s, r) => s + num(r.ied_num || r.ExplosivesIED), 0),
    explosivesIGCord: rows.reduce((s, r) => s + num(r.gcord_m || r.ExplosivesIGCord), 0),
    machineOperator: operators.length,
    estimatedTonnage: rows.reduce((s, r) => s + num(r.est_tonnage_t || r.EstimatedTonnage), 0),
    estimatedPowderFactor: rows.reduce((s, r) => s + num(r.est_powder_factor || r.EstimatedPowderFactor), 0),
    estimatedExplosives: rows.reduce((s, r) => s + num(r.est_explosives_kg || r.EstimatedExplosives), 0)
  };
}

export default SummaryChart;
